import { CloseIcon } from "@chakra-ui/icons";
import {
  Box,
  BoxProps,
  Button,
  Center,
  Flex,
  Grid,
  GridItem,
  Input,
  Spacer,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, {
  ChangeEventHandler,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { BsImages } from "react-icons/bs";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "wouter";

import { Pagination } from "@nens/chakra/components";
import {
  AuthMode,
  FilterSet,
  MetaModelsContext,
  fetchScenariosInFilters,
  useScenarios,
} from "@nens/ldo/api-v1";
import { LdoAuthContext } from "@nens/ldo/auth-v1";
import { ScenarioListItem } from "@nens/ldo/components";
import { useTableData } from "@nens/react/hooks";
import { objectFromEntries, useThrottledCallback } from "@nens/shared-utils";

import { AllCheckboxScenario } from "./all-checkbox-scenario";
import { BulkActionsMenu } from "./bulk-actions-menu";
import { GoToScenarioButton } from "./go-to-scenario-button";

interface ScenariosListProps extends BoxProps {
  authMode: AuthMode;
  filters: FilterSet;
  setDrawer: (state: boolean) => void;
  setFilters: React.Dispatch<React.SetStateAction<FilterSet>>;
  checkboxes: Record<number, true>;
  setCheckboxes: React.Dispatch<React.SetStateAction<Record<number, true>>>;
}

export function ScenariosList({
  authMode,
  filters,
  setFilters,
  setDrawer,
  checkboxes,
  setCheckboxes,
  ...rest
}: ScenariosListProps) {
  // Use this for pagination later
  const { page, limit, offset, pageSize, setPageSize, nextPage, previousPage, setPage } =
    useTableData({ initialPageSize: 15 });
  const setLocation = useLocation()[1];
  const intl = useIntl();

  const previousFilters = useRef<FilterSet>(filters);
  useEffect(() => {
    // When filters change, go back to page 1 and forget checkboxes.
    if (!filters.equals(previousFilters.current)) {
      setPage(1);
      setCheckboxes({});
      previousFilters.current = filters;
    }
  }, [authMode, filters, setPage, setCheckboxes]);

  const scenariosResponse = useScenarios(
    {
      mode: authMode,
      offset,
      limit,
    },
    filters
  );

  const { apiFetchFunction } = useContext(LdoAuthContext);
  const { getMetaModelById } = useContext(MetaModelsContext);
  const getAllScenariosInFilter = useCallback(
    () => fetchScenariosInFilters(apiFetchFunction, getMetaModelById, authMode, filters),
    [apiFetchFunction, getMetaModelById, authMode, filters]
  );

  const scenarios = scenariosResponse.isSuccess ? scenariosResponse.data.items : [];

  const numPages = scenariosResponse.isSuccess
    ? Math.ceil(scenariosResponse.data.total / pageSize)
    : 0;
  // We store it in a React state, so that we can continue showing the old number while loading
  // a new page; saves UI flickering.
  const [currentNumPages, setCurrentNumPages] = useState(numPages);
  if (scenariosResponse.isSuccess && numPages !== currentNumPages) {
    setCurrentNumPages(numPages);
  }

  const numChecked = Object.keys(checkboxes).length; // On all pages
  const allChecked = numChecked === scenariosResponse.data?.total;
  const someOnPageChecked =
    scenarios.length > 0 && scenarios.some((scenario) => checkboxes[scenario.id]);

  const allOnPageChecked =
    someOnPageChecked && scenarios.every((scenario) => checkboxes[scenario.id]);

  const checkAllOnPage = () => {
    setCheckboxes((checkboxes) => {
      const oldCheckboxes = { ...checkboxes };
      scenarios.forEach((scenario) => (oldCheckboxes[scenario.id] = true));
      return oldCheckboxes;
    });
  };

  const uncheckAllOnPage = () =>
    setCheckboxes((checkboxes) => {
      const oldCheckboxes = { ...checkboxes };
      scenarios.forEach((scenario) => {
        delete oldCheckboxes[scenario.id];
      });
      return oldCheckboxes;
    });

  const checkAll = async () => {
    const allScenarios = await getAllScenariosInFilter();
    setCheckboxes(objectFromEntries(allScenarios.map(({ id }) => [id, true])));
  };

  const uncheckAll = () => setCheckboxes({});

  const checkBox = (id: number) => (checked: boolean) => {
    if (checked) {
      setCheckboxes((checkboxes) => ({ ...checkboxes, [id]: true }));
    } else {
      setCheckboxes((checkboxes) => {
        const old = { ...checkboxes };
        delete old[id];
        return old;
      });
    }
  };
  const changeSearch: ChangeEventHandler<HTMLInputElement> = useThrottledCallback(
    (e) => setFilters((filters) => filters.set("search", e.target.value)),
    1000
  );

  return (
    <VStack mx={4} mt="20px" gap="0" {...rest}>
      <Flex w="full" justifyContent="space-between" alignItems="center" h="50px" gap={4} bg="white">
        <Flex alignItems="center">
          <Button
            onClick={() => setDrawer(true)}
            size="sm"
            display={{
              base: "block",
              md: "none",
            }}
          >
            Filters
          </Button>
          <Box mx="2">
            <BsImages color="#D68071" />
          </Box>
          <Text fontWeight="semibold" color="bij12_1.700">
            <FormattedMessage id="tableheader.scenarios" defaultMessage="Scenarios" />
          </Text>
          <Box
            color="bij12_1.700"
            fontWeight="semibold"
            bgColor={"#CC614D20"}
            fontSize={"sm"}
            rounded="full"
            px="14px"
            py="1px"
            ml="12px"
          >
            {scenariosResponse.data?.total.toString() || "0"}
          </Box>
        </Flex>
        {!filters.isEmpty() ? (
          <Flex>
            <Button
              size="sm"
              leftIcon={<CloseIcon boxSize={2} />}
              onClick={() => setFilters(new FilterSet({}))}
            >
              <FormattedMessage id="filtertree.reset" defaultMessage="Remove filters" />
            </Button>
          </Flex>
        ) : null}
        <Spacer />
        <Flex gap={4}>
          <GoToScenarioButton />
          <Input
            size="sm"
            colorScheme="whiteAlpha"
            bgColor="white"
            w="400px"
            mr="10px"
            placeholder={intl.formatMessage({
              id: "search.placeholder",
              defaultMessage: "Type here to filter",
            })}
            onChange={changeSearch}
          />
        </Flex>
      </Flex>
      <Grid
        templateAreas={`
        "checkbox col col col"
        `}
        gridTemplateColumns={`20px 0.8fr 100px 200px`}
        gap={4}
        py={2}
        px={4}
        w="100%"
        position="sticky"
        bg="#FCF5F4"
        zIndex="98"
      >
        <GridItem w="100%" area="checkbox">
          <Flex alignItems="center" h="full">
            <AllCheckboxScenario
              allChecked={allChecked}
              allOnPageChecked={allOnPageChecked}
              someOnPageChecked={someOnPageChecked}
              noScenarios={scenarios.length === 0}
              checkAllOnPage={checkAllOnPage}
              uncheckAllOnPage={uncheckAllOnPage}
              checkAll={checkAll}
              uncheckAll={uncheckAll}
            />
          </Flex>
        </GridItem>
        <GridItem w="100%">
          <Flex alignItems="center" mt="6px" gap={4}>
            <BulkActionsMenu
              authMode={authMode}
              scenarioIds={Object.keys(checkboxes)}
              getAllScenariosInFilter={getAllScenariosInFilter}
            />
            <Text>
              {
                /* react-intl is way too complicated for plurals imo */
                numChecked === 1 ? (
                  <FormattedMessage
                    id="listpage.numchecked.one"
                    defaultMessage="1 scenario selected"
                  />
                ) : (
                  <FormattedMessage
                    id="listpage.numchecked.other"
                    defaultMessage="{numChecked} scenarios selected"
                    values={{ numChecked }}
                  />
                )
              }
            </Text>
          </Flex>
        </GridItem>
        <GridItem w="100%" h="10">
          <Flex h="10" alignItems="center">
            <Button
              variant="ghost"
              px="0"
              _hover={{ bgColor: "none" }}
              _active={{ bgColor: "none" }}
            >
              ID
            </Button>
          </Flex>
        </GridItem>
        <GridItem w="100%" h="10">
          <Flex h="10" alignItems="center">
            <Button
              variant="ghost"
              px="0"
              _hover={{ bgColor: "none" }}
              _active={{ bgColor: "none" }}
            >
              {authMode === "public" ? (
                <FormattedMessage id="listpage.owner_column" defaultMessage="Owner" />
              ) : (
                "Status"
              )}
            </Button>
          </Flex>
        </GridItem>
      </Grid>

      <Box w="100%">
        {scenarios.map((scenario) => (
          <ScenarioListItem
            key={scenario.id}
            authMode={authMode}
            scenario={scenario}
            checkbox
            checked={!!checkboxes[scenario.id]}
            setChecked={checkBox(scenario.id)}
            onSelect={() => {
              setLocation(`/scenarios/${scenario.id}`);
            }}
          />
        ))}
        {scenariosResponse.isFetching ? (
          <Center h={`${60 * pageSize}px`}>
            <Spinner size="xl" />
          </Center>
        ) : scenarios.length === 0 ? (
          <Center h={`${60 * pageSize}px`}>
            <Text>
              <FormattedMessage id="table.empty" defaultMessage="There are 0 results." />
            </Text>
          </Center>
        ) : null}
      </Box>
      <Pagination
        toPage1={() => setPage(1)}
        toLastPage={() => setPage(numPages)}
        setItemsPerPage={setPageSize}
        itemsPerPageChoices={[15, 30, 60, 100]}
        toPrevious={previousPage}
        toNext={page < numPages ? nextPage : undefined}
        page={page}
        numPages={currentNumPages}
      />
    </VStack>
  );
}
